<script setup>
import { onMounted } from 'vue';
import { formatDate, getMetaTags } from '~/utils/utils.js';
import PageTitle from '~/components/PageTitle.vue';
import PriceOverview from '~/components/snapshot/PriceOverview.vue';
import ChangesAndHighsAndLows from '~/components/snapshot/ChangesAndHighsAndLows.vue';
import MarketMetrics from '~/components/snapshot/MarketMetrics.vue';
import SupplyMetrics from '~/components/snapshot/SupplyMetrics.vue';
import SupplyDoughnutCharts from '~/components/tokenomics/SupplyDoughnutCharts.vue';
import MiningMetrics from '~/components/snapshot/MiningMetrics.vue';
import MiningReward from '~/components/snapshot/MiningReward.vue';
import KaspaPriceChart from '~/components/snapshot/KaspaPriceChart.vue';
import AddressesByBalance from '~/components/tokenomics/AddressesByBalance.vue';
import HoldingPeriods from '~/components/tokenomics/HoldingPeriods.vue';
import StockToFlowChart from '~/components/tokenomics/StockToFlowChart.vue';
import KaspaCurrencies from '~/components/snapshot/KaspaCurrencies.vue';

// Add meta tags for SEO
useHead(getMetaTags({
    title: 'Snapshot | KaspHub',
    description: 'Get live Kaspa cryptocurrency analytics, real-time price data, market metrics, mining statistics, and comprehensive tokenomics. Your all-in-one Kaspa monitoring solution.'
}));

// Store
const snapshotStore = useSnapshot();

// Load data
const loadSnapshotData = async () => {
    if (snapshotStore.value.initCallDone) return;
    
    try {
        // Fetch data
        const data = await $fetch('/api/snapshot');
        
        // Update store
        snapshotStore.value.snapshotData = data;
        snapshotStore.value.initCallDone = true;
    } catch (error) {
        console.error('Failed to fetch data:', error);
    }
};

// On component mounted
onMounted(() => {
    loadSnapshotData(); // Initial load
});
</script>

<template>
    <div>
        <!-- Page Title -->
        <PageTitle title="Snapshot" />

        <!-- Price Overview Section -->
        <div class="mb-7">
            <!-- Section Header -->
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0 mb-4">
                <!-- Section Title -->
                <h2 class="text-xl font-bold text-gray-900 dark:text-white">Price Overview</h2>

                <!-- Last Updated -->
                <p class="text-sm text-gray-500 dark:text-gray-400">
                    Last Updated: {{ formatDate(snapshotStore.snapshotData?.lastUpdated) }}
                </p>
            </div>

            <!-- Grid -->
            <div class="space-y-4 md:space-y-5 scraper-kaspa-price-overview">
                <!-- Price Overview -->
                <PriceOverview :price="snapshotStore.snapshotData?.price"
                    :hashrate-change-7d="snapshotStore.snapshotData?.hashrateChange7d"
                    :hashrate-change-30d="snapshotStore.snapshotData?.hashrateChange30d"
                    :hashrate-change-90d="snapshotStore.snapshotData?.hashrateChange90d"
                    :loading="!snapshotStore.initCallDone" />

                <!-- Historical Highs and Lows -->
                <ChangesAndHighsAndLows :price-change-24h="snapshotStore.snapshotData?.priceChange24h"
                    :price-change-7d="snapshotStore.snapshotData?.priceChange7d"
                    :price-change-30d="snapshotStore.snapshotData?.priceChange30d"
                    :all-time-high="snapshotStore.snapshotData?.allTimeHigh"
                    :all-time-high-date="snapshotStore.snapshotData?.allTimeHighDate"
                    :all-time-high-change-percentage="snapshotStore.snapshotData?.allTimeHighChangePercentage"
                    :all-time-high-change-value="snapshotStore.snapshotData?.allTimeHighChangeValue"
                    :all-time-low="snapshotStore.snapshotData?.allTimeLow"
                    :all-time-low-date="snapshotStore.snapshotData?.allTimeLowDate"
                    :all-time-low-change-percentage="snapshotStore.snapshotData?.allTimeLowChangePercentage"
                    :all-time-low-change-value="snapshotStore.snapshotData?.allTimeLowChangeValue"
                    :loading="!snapshotStore.initCallDone" />

                <!-- Price Chart -->
                <KaspaPriceChart :kaspa-price-history-7d="snapshotStore.snapshotData?.kaspaPriceHistory7d"
                    :kaspa-price-history-30d="snapshotStore.snapshotData?.kaspaPriceHistory30d"
                    :kaspa-price-history-90d="snapshotStore.snapshotData?.kaspaPriceHistory90d"
                    :loading="!snapshotStore.initCallDone" />
            </div>
        </div>

        <!-- Price Around The World Section -->
        <div class="mb-7">
            <!-- Section Header -->
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0 mb-4">
                <!-- Section Title -->
                <h2 class="text-xl font-bold text-gray-900 dark:text-white">Price Around The World</h2>
            </div>

            <!-- Grid -->
            <div class="space-y-4 md:space-y-5 scraper-kaspa-currencies">
                <!-- Price In Different Currencies -->
                <KaspaCurrencies :currencies="snapshotStore.snapshotData?.priceInCurrencies"
                    :loading="!snapshotStore.initCallDone" />
            </div>
        </div>

        <!-- Market Metrics Section -->
        <div class="mb-7">
            <!-- Section Header -->
            <div class="mb-4">
                <!-- Section Title -->
                <h2 class="text-xl font-bold text-gray-900 dark:text-white">Market Metrics</h2>
            </div>

            <!-- Grid -->
            <div class="space-y-4 md:space-y-5">
                <!-- Market Metrics -->
                <MarketMetrics :market-cap="snapshotStore.snapshotData?.marketCap"
                    :market-cap-rank="snapshotStore.snapshotData?.marketCapRank"
                    :volume-24h="snapshotStore.snapshotData?.volume24h"
                    :fully-diluted-valuation="snapshotStore.snapshotData?.fullyDilutedValuation"
                    :loading="!snapshotStore.initCallDone" />
            </div>
        </div>

        <!-- Supply Metrics Section -->
        <div class="mb-7">
            <!-- Section Header -->
            <div class="mb-4">
                <!-- Section Title -->
                <h2 class="text-xl font-bold text-gray-900 dark:text-white">Supply Metrics</h2>
            </div>

            <!-- Grid -->
            <div class="space-y-4 md:space-y-5 scraper-supply-metrics">
                <!-- Supply Metrics -->
                <SupplyMetrics :circulating-supply="snapshotStore.snapshotData?.circulatingSupply"
                    :total-supply="snapshotStore.snapshotData?.totalSupply"
                    :max-supply="snapshotStore.snapshotData?.maxSupply"
                    :loading="!snapshotStore.initCallDone" />

                <!-- Supply Doughnut Charts -->
                <SupplyDoughnutCharts :current-mined-percentage="snapshotStore.snapshotData?.currentMinedPercentage"
                    :six-month-mined-percentage="snapshotStore.snapshotData?.sixMonthMinedPercentage"
                    :one-year-mined-percentage="snapshotStore.snapshotData?.oneYearMinedPercentage"
                    :loading="!snapshotStore.initCallDone" />
            </div>
        </div>

        <!-- Reward Data Section -->
        <div class="mb-7">
            <!-- Section Header -->
            <div class="mb-4">
                <!-- Section Title -->
                <h2 class="text-xl font-bold text-gray-900 dark:text-white">Reward Data</h2>
            </div>

            <!-- Grid -->
            <div class="space-y-4 md:space-y-5 scraper-reward-data">
                <!-- Mining Reward -->
                <MiningReward :current-block-reward="snapshotStore.snapshotData?.currentBlockReward"
                    :current-block-reward-usd="snapshotStore.snapshotData?.currentBlockRewardUsd"
                    :next-block-reward="snapshotStore.snapshotData?.nextBlockReward"
                    :next-block-reward-usd="snapshotStore.snapshotData?.nextBlockRewardUsd"
                    :next-halving-date="snapshotStore.snapshotData?.nextHalvingDate"
                    :reward-percentage-decrease="snapshotStore.snapshotData?.rewardPercentageDecrease"
                    :loading="!snapshotStore.initCallDone" />

                <!-- Mining Metrics -->
                <MiningMetrics :hashrate="snapshotStore.snapshotData?.hashrate"
                    :daily-mining-reward-kas="snapshotStore.snapshotData?.dailyMiningRewardKas"
                    :daily-mining-reward-usd="snapshotStore.snapshotData?.dailyMiningRewardUsd"
                    :loading="!snapshotStore.initCallDone" />
            </div>
        </div>

        <!-- Kaspa Distribution Section -->
        <div class="mb-7">
            <!-- Section Header -->
            <div class="mb-4">
                <!-- Section Title -->
                <h2 class="text-xl font-bold text-gray-900 dark:text-white">Kaspa Distribution</h2>
            </div>

            <!-- Grid -->
            <div class="space-y-4 md:space-y-5">
                <!-- Addresses By Balance -->
                <AddressesByBalance class="scraper-addresses-by-balance"
                    :address-ranges="snapshotStore.snapshotData?.balanceStats"
                    :loading="!snapshotStore.initCallDone" />

                <!-- Holding Periods -->
                <HoldingPeriods class="scraper-holding-periods"
                    :holding-periods="snapshotStore.snapshotData?.holdingPeriodStats"
                    :loading="!snapshotStore.initCallDone" />
            </div>
        </div>

        <!-- Stock-To-Flow Section -->
        <div>
            <!-- Section Header -->
            <div class="mb-4">
                <h2 class="text-xl font-bold text-gray-900 dark:text-white">Stock-to-Flow</h2>
            </div>

            <!-- Grid -->
            <div class="space-y-4 md:space-y-5 scraper-stock-to-flow">
                <!-- Stock-To-Flow Chart -->
                <StockToFlowChart :kaspa-stock-to-flow="snapshotStore.snapshotData?.kaspaStockToFlow"
                    :loading="!snapshotStore.initCallDone" />
            </div>
        </div>
    </div>
</template>